import {
  FunctionComponent,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react";
import { useFormik } from "formik";
// import { useNavigate } from "react-router-dom";
import styled from "styled-components";
// import useDebitor from "~/hooks/useDebitor";
import usePartner from "~/hooks/usePartner";
import useOperation from "~/hooks/useOperation";
import CloseIcon from "~/assets/Close--outline.png";
import SaveIcon from "~/assets/Save.png";

import {
  getPartnersAPI,
  IPartner,
  postPartnerBankAccountAPI,
} from "~/services/api/partner";
import {
  Button,
  // Button,
  Input,
  InputSelect,
  Margin,
  // Tooltip,
} from "prosperita-dumbo-react";
// import NewDebitorIcon from "~/assets/new-debitor.png";
// import ArrowIcon from "~/assets/arrow-right-_1_.png";
import { getLabelByValue, onlyNumbers, zIndex } from "~/services/utils";
// import ArrowLeft from "~/assets/arrow-lftt-_1_.png";
// import WhiteLeft from "~/assets/white-left.png";
import { getBankAccountsAPI, IBankAccount } from "~/services/api/bankAccount";
import { toast } from "react-toastify";
import useLoading from "~/hooks/useLoading";
import documentNumberValidator from "~/services/validateDocumentNumber";
import LateralModal from "~/components/LateralModal";
import { banksOptions } from "~/utils/banks";

const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 32px;
  row-gap: 30px;
  margin-top: 20px;
  overflow: hidden;

`;

const Footer = styled.div`
  width: 90%;
  position: fixed;
  bottom: 0;
  display: grid;
  padding-bottom: 50px;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
`;

type IInitialValues = {
  debtor_uuid: string;
  liquidation_bank_account_uuid: string;
  liquidation_type: string;
  commercial_note_series: string;
  commercial_note_nature: string;
  invoice_barcode: string;
  pix_key: string;
  pix_key_type: string;
  agency: string;
  agency_digit: string;
  account_number: string;
  account_number_digit: string;
  bank_identification_code: string;
};

const ProductInfo: FunctionComponent = () => {
  // const { setOpenNewDebitorModal } = useDebitor();
  const {
    setPartners,
    partners,
    setBankAccounts,
    bankAccounts,
    setAddBankAccounts,
    addBankAccounts,
  } = usePartner();
  const { setPayloadOperationCommercialNote, setTab, setCanAdvance } =
    useOperation();
  const { setLoading } = useLoading();
  // const navigate = useNavigate();
  // const [hover, setHover] = useState<boolean>(false);
  const [showRequiredErrors, setShowRequiredErrors] = useState<boolean>(false);
  const previousDebtorUuidRef = useRef<string>("");
  const initialValues = useMemo(() => {
    const storedOperation = localStorage.getItem("@:operation");
    const defaultValues: IInitialValues = {
      debtor_uuid: "",
      liquidation_bank_account_uuid: "",
      liquidation_type: "",
      invoice_barcode: "",
      commercial_note_series: "",
      commercial_note_nature: "",
      pix_key: "",
      pix_key_type: "",
      agency: "",
      agency_digit: "",
      account_number: "",
      account_number_digit: "",
      bank_identification_code: "",
    };

    if (storedOperation) {
      try {
        const parsedOperation = JSON.parse(storedOperation);
        // Se for "pix", infere se é chave ou conta bancária baseado no pix_key_type
        let liquidationType = parsedOperation.liquidation_type;
        if (liquidationType === "pix") {
          // Se pix_key_type é "bank_account", é conta bancária
          if (parsedOperation.pix_key_type === "bank_account") {
            liquidationType = "pix_bank_account";
          } else if (parsedOperation.pix_key && parsedOperation.pix_key_type) {
            liquidationType = "pix_key";
          } else {
            liquidationType = "pix_key"; // Padrão
          }
        }
        return {
          debtor_uuid: parsedOperation.debtor_uuid,
          liquidation_bank_account_uuid:
            parsedOperation.liquidation_bank_account_uuid,
          liquidation_type: liquidationType,
          commercial_note_series: parsedOperation.commercial_note_series,
          commercial_note_nature: parsedOperation.commercial_note_nature,
          invoice_barcode: parsedOperation.invoice_barcode,
          pix_key: parsedOperation.pix_key,
          // Se for pix_bank_account, não carrega o pix_key_type (será definido como "bank_account" automaticamente)
          pix_key_type: liquidationType === "pix_bank_account" ? "" : parsedOperation.pix_key_type,
          agency: "",
          agency_digit: "",
          account_number: "",
          account_number_digit: "",
          bank_identification_code: "",
        };
      } catch (error) {
        console.error("Erro ao parsear o localStorage:", error);
      }
    }

    return defaultValues;
  }, []);

  const { values, setFieldValue } = useFormik({
    initialValues,
    onSubmit: () => undefined,
  });

  // Inicializa a referência com o valor inicial do tomador
  useEffect(() => {
    if (previousDebtorUuidRef.current === "" && values.debtor_uuid !== "") {
      previousDebtorUuidRef.current = values.debtor_uuid;
    }
  }, [values.debtor_uuid]);

  const handleSelect = useCallback(
    (fieldName: keyof IInitialValues, selectedOption) => {
      if (selectedOption) {
        setFieldValue(fieldName, selectedOption.value);
      }
    },
    [setFieldValue]
  );

  const fetchPartners = useCallback(async (): Promise<void> => {
    try {
      const { data } = await getPartnersAPI();
      setPartners(data.payload);
    } catch (e) {
      console.log(e);
    }
  }, [setPartners]);

  const partnersOptions = useMemo(() => {
    return partners
      .map((partner: IPartner) => ({
        value: partner.uuid,
        label: partner.name,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [partners]);

  const liquidationTypeOptions = useMemo(() => {
    return [
      { value: "pix_key", label: "Pix (Chave)" },
      { value: "pix_bank_account", label: "Pix (Conta bancária)" },
      { value: "billet", label: "Boleto" },
      { value: "ted", label: "TED" },
    ].sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
    );
  }, []);

  const typeKeyPixOptions = useMemo(() => {
    return [
      { value: "document_number_cpf", label: "CPF" },
      { value: "document_number_cnpj", label: "CNPJ" },
      { value: "phone", label: "Celular" },
      { value: "email", label: "Email" },
      { value: "evp", label: "Chave Aleatória" },
    ];
  }, []);

  const noteTypeOptions = useMemo(() => {
    return [
      { value: "promissory_note", label: "Nota Promissória" },
      { value: "bill_of_exchange", label: "Letra de Câmbio" },
      { value: "commercial_paper", label: "Papel Comercial" },
      { value: "debenture", label: "Debênture" },
      { value: "bond", label: "Título de Dívida (Bond)" },
      { value: "convertible_note", label: "Nota Conversível" },
      { value: "secured_note", label: "Nota Garantida" },
      { value: "unsecured_note", label: "Nota Não Garantida" },
      { value: "treasury_note", label: "Nota do Tesouro (T-Note)" },
      { value: "subordinated_note", label: "Nota Subordinada" },
      { value: "senior_note", label: "Nota Sênior" },
      { value: "bearer_note", label: "Nota ao Portador" },
      { value: "registered_note", label: "Nota Registrada" },
      { value: "floating_rate_note", label: "Nota de Taxa Flutuante (FRN)" },
      { value: "fixed_rate_note", label: "Nota de Taxa Fixa" },
      { value: "zero_coupon_note", label: "Nota de Cupom Zero" },
      { value: "structured_note", label: "Nota Estruturada" },
      { value: "negotiable_instrument", label: "Instrumento Negociável" },
    ].sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
    );
  }, []);

  const bankAccountOptions = useMemo(() => {
    return bankAccounts
      .map((bankAccount: IBankAccount) => ({
        value: bankAccount.uuid,
        label: `${bankAccount.bank_name} - Agência: ${bankAccount.agency}${bankAccount.agency_digit ? `-${bankAccount.agency_digit}` : ""} Conta: ${bankAccount.account_number}-${bankAccount.account_number_digit}`,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [bankAccounts]);

  const fetchBankAccounts = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await getBankAccountsAPI(values.debtor_uuid);
      setBankAccounts(data.payload);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  }, [values.debtor_uuid, setBankAccounts, setLoading]);

  const handleSelectTypeKeyPix = useCallback(
    (fieldName: keyof IInitialValues, selectedOption) => {
      if (selectedOption) {
        setFieldValue("pix_key", "");
        setFieldValue(fieldName, selectedOption.value);
      }
    },
    [setFieldValue]
  );

  const formatCPF = (value: string): string => {
    value = value.replace(/\D/g, "").slice(0, 11); // <-- limita a 11 dígitos
    return value
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  };

  const formatCNPJ = (value: string): string => {
    value = value.replace(/\D/g, "");
    if (value.length <= 2) return value;
    if (value.length <= 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
    if (value.length <= 8)
      return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
    if (value.length <= 12)
      return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8)}`;
    return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12, 14)}`;
  };

  const formatPhone = (value: string): string => {
    value = value.replace(/\D/g, "");
    if (value.length <= 2) return `(${value}`;
    if (value.length <= 6) return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
  };

  const handlePixKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    let formattedValue = rawValue;

    switch (values.pix_key_type) {
      case "document_number_cpf":
        formattedValue = formatCPF(rawValue);
        break;
      case "document_number_cnpj":
        formattedValue = formatCNPJ(rawValue);
        break;
      case "phone":
        formattedValue = formatPhone(rawValue);
        break;
      default:
        formattedValue = rawValue;
    }

    setFieldValue("pix_key", formattedValue);
  };

  const handleClose = useCallback(() => {
    setAddBankAccounts(false);

    setFieldValue("agency", "");
    setFieldValue("agency_digit", "");
    setFieldValue("account_number", "");
    setFieldValue("account_number_digit", "");
    setFieldValue("bank_identification_code", "");
  }, [setAddBankAccounts, setFieldValue]);

  const createBankAccount = useCallback(async () => {
    const payload = {
      agency: values.agency,
      agency_digit: values.agency_digit,
      account_number: values.account_number,
      account_number_digit: values.account_number_digit,
      bank_code: values.bank_identification_code.split("/*?")[0],
      partner_uuid: values.debtor_uuid,
    };
    setLoading(true);

    try {
      await postPartnerBankAccountAPI(payload);

      toast.success("Conta bancária adicionada com sucesso");
      handleClose();

      fetchBankAccounts();
      setLoading(false);
    } catch (e) {
      console.log(e);

      toast.error(
        "Não foi possível adicionar a conta bancária. Tente novamente"
      );
      setLoading(false);
    }
  }, [values, setLoading, fetchBankAccounts, handleClose]);

  const isValidCPF = useMemo(() => {
    return (
      values.pix_key_type === "document_number_cpf" &&
      onlyNumbers(values.pix_key).length > 10 &&
      documentNumberValidator(values.pix_key)
    );
  }, [values]);

  const isValidCNPJ = useMemo(() => {
    return (
      values.pix_key_type === "document_number_cnpj" &&
      onlyNumbers(values.pix_key).length > 13 &&
      documentNumberValidator(values.pix_key)
    );
  }, [values]);

  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  useEffect(() => {
    // Sempre salva como "pix" no localStorage, mas mantém os campos para diferenciar
    const liquidationTypeForStorage = 
      values.liquidation_type === "pix_key" || values.liquidation_type === "pix_bank_account"
        ? "pix"
        : values.liquidation_type;

    // Define pix_key_type como "bank_account" quando for PIX conta bancária
    let pixKeyTypeForStorage = values.pix_key_type;
    if (values.liquidation_type === "pix_bank_account") {
      pixKeyTypeForStorage = "bank_account";
    }

    const operationData = {
      debtor_uuid: values.debtor_uuid,
      liquidation_bank_account_uuid: values.liquidation_bank_account_uuid,
      liquidation_type: liquidationTypeForStorage, // Sempre "pix" quando for PIX
      commercial_note_nature: values.commercial_note_nature,
      commercial_note_series: values.commercial_note_series,
      invoice_barcode: values.invoice_barcode,
      pix_key_type: pixKeyTypeForStorage, // "bank_account" quando for conta bancária
      pix_key: values.pix_key,
    };

    localStorage.setItem("@:operation", JSON.stringify(operationData));
    
    // Para o contexto, mantém o valor original para controle da UI
    setPayloadOperationCommercialNote({
      ...operationData,
      liquidation_type: values.liquidation_type,
    });
  }, [values, setPayloadOperationCommercialNote]);

  useEffect(() => {
    fetchPartners();
    setTab(1);
  }, [fetchPartners, setTab]);

  useEffect(() => {
    // Só limpa a conta bancária se o tomador realmente mudou
    if (previousDebtorUuidRef.current !== "" && previousDebtorUuidRef.current !== values.debtor_uuid) {
      // Limpa a conta bancária selecionada quando o tomador muda
      setFieldValue("liquidation_bank_account_uuid", "");
      // Limpa o array de contas bancárias
      setBankAccounts([]);
    }
    
    // Atualiza a referência do tomador anterior
    previousDebtorUuidRef.current = values.debtor_uuid;
    
    if (values.debtor_uuid !== "") {
      fetchBankAccounts();
    }
  }, [values.debtor_uuid, fetchBankAccounts, setFieldValue, setBankAccounts]);

  useEffect(() => {
    if (values.liquidation_type !== "billet") {
      setFieldValue("invoice_barcode", "");
    }

    // Limpa campos de chave pix quando não for pix_key
    if (values.liquidation_type !== "pix_key") {
      // Se mudar para pix_bank_account, não limpa o pix_key_type aqui (será definido como "bank_account" no save)
      // Se mudar para outro tipo que não seja pix, limpa tudo
      if (values.liquidation_type !== "pix_bank_account") {
        setFieldValue("pix_key_type", "");
      }
      setFieldValue("pix_key", "");
    }

    // Limpa conta bancária quando for pix_key
    if (values.liquidation_type === "pix_key") {
      setFieldValue("liquidation_bank_account_uuid", "");
      // Limpa pix_key_type quando muda para pix_key (usuário vai selecionar depois)
      setFieldValue("pix_key_type", "");
    }
  }, [values.liquidation_type, setFieldValue]);

  const banks = banksOptions.map((item) => ({
    value: `${item.code}/*?${item.name}`,
    label: item.name,
  }));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const hasFormErrors = useMemo(() => {
    // Validações para PIX com chave
    const pixKeyErrors = values.liquidation_type === "pix_key" && (
      !values.pix_key_type ||
      !values.pix_key ||
      (values.pix_key_type === "phone" && onlyNumbers(values.pix_key).length < 11) ||
      (values.pix_key_type === "email" && !validateEmail(values.pix_key)) ||
      (values.pix_key_type === "document_number_cpf" && onlyNumbers(values.pix_key).length < 11) ||
      (values.pix_key_type === "document_number_cnpj" && onlyNumbers(values.pix_key).length < 14) ||
      (values.pix_key_type === "document_number_cnpj" && onlyNumbers(values.pix_key).length === 14 && !isValidCNPJ) ||
      (values.pix_key_type === "document_number_cpf" && onlyNumbers(values.pix_key).length === 11 && !isValidCPF)
    );

    return (
      values.debtor_uuid === "" ||
      !values.liquidation_type ||
      !values.commercial_note_nature ||
      !values.commercial_note_series ||
      (values.liquidation_type === "billet" && values.invoice_barcode === "") ||
      // Para TED e PIX com conta bancária, precisa de conta bancária
      ((values.liquidation_type === "ted" || values.liquidation_type === "pix_bank_account") && !values.liquidation_bank_account_uuid) ||
      pixKeyErrors
    );
  }, [values, isValidCNPJ, isValidCPF, validateEmail]);

  const validateAndAdvance = useCallback((): boolean => {
    setShowRequiredErrors(true);
    
    if (hasFormErrors) {
      // Encontrar o primeiro campo com erro e focar nele
      const fieldIds = [
        "debtor_uuid",
        "liquidation_type",
        "commercial_note_nature",
        "commercial_note_series",
        "invoice_barcode",
        "liquidation_bank_account_uuid",
        "pix_key_type",
        "pix_key",
      ];
      
      for (const fieldId of fieldIds) {
        const element = document.getElementById(fieldId) || 
                       document.querySelector(`[name="${fieldId}"]`);
        if (element) {
          (element as HTMLElement).focus();
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        }
      }
      
      return false;
    }
    
    return true;
  }, [hasFormErrors]);

  // Expor a função de validação globalmente para o componente pai acessar
  useEffect(() => {
    (window as any).validateProductInfo = validateAndAdvance;
    return () => {
      delete (window as any).validateProductInfo;
    };
  }, [validateAndAdvance]);

  return (
    <Container>
      <div>
        {" "}
        <InputSelect
          name="debtor_uuid"
          id="debtor_uuid"
          kind="default"
          size="small"
          label="Tomador"
          options={partnersOptions}
          error={showRequiredErrors && !values.debtor_uuid}
          message={showRequiredErrors && !values.debtor_uuid ? "Campo obrigatório" : ""}
          placeholder={
            values.debtor_uuid
              ? getLabelByValue(values.debtor_uuid, partnersOptions)
              : "Selecione uma opção"
          }
          onChange={(e) => {
            handleSelect("debtor_uuid", e);
          }}
        />
      </div>

      <div>
        <InputSelect
          name="liquidation_type"
          id="liquidation_type"
          kind="default"
          size="small"
          label="Tipo de liquidação"
          options={liquidationTypeOptions}
          error={showRequiredErrors && !values.liquidation_type}
          message={showRequiredErrors && !values.liquidation_type ? "Campo obrigatório" : ""}
          placeholder={
            values.liquidation_type
              ? getLabelByValue(values.liquidation_type, liquidationTypeOptions)
              : "Selecione uma opção"
          }
          onChange={(e) => handleSelect("liquidation_type", e)}
        />
      </div>
      {values.liquidation_type === "billet" && (
        <Input
          name="invoice_barcode"
          id="invoice_barcode"
          size="large"
          value={values.invoice_barcode}
          type="text"
          max={100}
          maxLength={100}
          label="Código de barras do boleto"
          placeholder="Digite"
          error={showRequiredErrors && values.liquidation_type === "billet" && !values.invoice_barcode}
          message={showRequiredErrors && values.liquidation_type === "billet" && !values.invoice_barcode ? "Campo obrigatório" : ""}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");

            setFieldValue("invoice_barcode", value);
          }}
        />
      )}
      {/* <div
        style={{
          position: "relative",
          top: "23px",
        }}
      >
        <Tooltip align="top-right" size="medium" label="Adicionar contato">
          {" "}
          <Button
            size="medium"
            kind="tertiary"
            icon={NewDebitorIcon}
            onClick={() => setOpenNewDebitorModal(true)}
          />
        </Tooltip>
      </div> */}
      {/* Mostra conta bancária para TED, Boleto e PIX com conta bancária */}
      {(values.liquidation_type === "ted" ||
        values.liquidation_type === "billet" ||
        values.liquidation_type === "pix_bank_account") && (
        <div>
          <InputSelect
            name="liquidation_bank_account_uuid"
            id="liquidation_bank_account_uuid"
            kind="default"
            size="small"
            label="Conta de liquidação"
            options={bankAccountOptions}
            error={showRequiredErrors && (values.liquidation_type === "ted" || values.liquidation_type === "pix_bank_account" || values.liquidation_type === "billet") && !values.liquidation_bank_account_uuid}
            message={showRequiredErrors && (values.liquidation_type === "ted" || values.liquidation_type === "pix_bank_account" || values.liquidation_type === "billet") && !values.liquidation_bank_account_uuid ? "Campo obrigatório" : ""}
            onChange={(e) => handleSelect("liquidation_bank_account_uuid", e)}
            placeholder={
              bankAccountOptions.length > 0 &&
              values.debtor_uuid &&
              values.liquidation_bank_account_uuid
                ? getLabelByValue(
                    values.liquidation_bank_account_uuid,
                    bankAccountOptions
                  )
                : !values.debtor_uuid
                  ? "Primeiro selecione um tomador"
                  : bankAccountOptions.length <= 0 && values.debtor_uuid
                    ? "O tomador não possui conta bancária"
                    : "Selecione uma opção"
            }
            disabled={bankAccountOptions.length <= 0}
          />
          {values.debtor_uuid && (
            <p
              style={{
                color: "blue",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => setAddBankAccounts(true)}
            >
              Adicionar conta bancária
            </p>
          )}
        </div>
      )}
      {/* Mostra campos de chave PIX apenas quando for pix_key */}
      {values.liquidation_type === "pix_key" && (
        <div>
          <InputSelect
            name="pix_key_type"
            id="pix_key_type"
            kind="default"
            size="small"
            label="Tipo de chave"
            options={typeKeyPixOptions}
            error={showRequiredErrors && values.liquidation_type === "pix_key" && !values.pix_key_type}
            message={showRequiredErrors && values.liquidation_type === "pix_key" && !values.pix_key_type ? "Campo obrigatório" : ""}
            onChange={(e) => handleSelectTypeKeyPix("pix_key_type", e)}
            placeholder={
              values.pix_key_type
                ? getLabelByValue(values.pix_key_type, typeKeyPixOptions)
                : "Selecione uma opção"
            }
          />
        </div>
      )}
      {values.pix_key_type && values.liquidation_type === "pix_key" && (
        <Input
          size="large"
          name="pix_key"
          id="pix_key"
          label="* Chave Pix"
          error={
            (showRequiredErrors && values.liquidation_type === "pix_key" && !values.pix_key) ||
            (showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "phone" && onlyNumbers(values.pix_key).length < 11) ||
            (showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "email" && !validateEmail(values.pix_key)) ||
            (showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "document_number_cpf" && onlyNumbers(values.pix_key).length < 11) ||
            (showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "document_number_cnpj" && onlyNumbers(values.pix_key).length < 14) ||
            (values.pix_key_type === "document_number_cpf" &&
              onlyNumbers(values.pix_key).length === 11 &&
              !isValidCPF) ||
            (values.pix_key_type === "document_number_cnpj" &&
              onlyNumbers(values.pix_key).length === 14 &&
              !isValidCNPJ)
          }
          message={
            showRequiredErrors && values.liquidation_type === "pix_key" && !values.pix_key
              ? "Campo obrigatório"
              : showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "phone" && onlyNumbers(values.pix_key).length < 11
                ? "Telefone deve ter 11 dígitos"
                : showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "email" && !validateEmail(values.pix_key)
                  ? "Email inválido"
                  : showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "document_number_cpf" && onlyNumbers(values.pix_key).length < 11
                    ? "CPF deve ter 11 dígitos"
                    : showRequiredErrors && values.liquidation_type === "pix_key" && values.pix_key_type === "document_number_cnpj" && onlyNumbers(values.pix_key).length < 14
                      ? "CNPJ deve ter 14 dígitos"
                      : values.pix_key_type === "document_number_cpf" &&
                          onlyNumbers(values.pix_key).length === 11 &&
                          !isValidCPF
                        ? "CPF Inválido"
                        : values.pix_key_type === "document_number_cnpj" &&
                            onlyNumbers(values.pix_key).length === 14 &&
                            !isValidCNPJ
                          ? "CNPJ Inválido"
                          : ""
          }
          placeholder={
            values.pix_key_type === "document_number_cpf"
              ? "000.000.000-00"
              : values.pix_key_type === "document_number_cnpj"
                ? "00.000.000/0000-00"
                : values.pix_key_type === "phone"
                  ? "(00) 00000-0000"
                  : "Digite sua chave Pix"
          }
          value={values.pix_key}
          onChange={(e) => handlePixKeyChange(e)}
        />
      )}
      <div>
        <InputSelect
          name="commercial_note_nature"
          id="commercial_note_nature"
          kind="default"
          size="small"
          label="Espécie da nota"
          options={noteTypeOptions}
          error={showRequiredErrors && !values.commercial_note_nature}
          message={showRequiredErrors && !values.commercial_note_nature ? "Campo obrigatório" : ""}
          placeholder={
            values.commercial_note_nature
              ? getLabelByValue(values.commercial_note_nature, noteTypeOptions)
              : "Selecione uma opção"
          }
          onChange={(e) => handleSelect("commercial_note_nature", e)}
        />
      </div>

      <Input
        max={50}
        maxLength={50}
        name="commercial_note_series"
        id="commercial_note_series"
        size="large"
        label="Série da nota"
        placeholder="Digite"
        value={values.commercial_note_series}
        error={showRequiredErrors && !values.commercial_note_series}
        message={showRequiredErrors && !values.commercial_note_series ? "Campo obrigatório" : ""}
        onChange={(e) =>
          setFieldValue("commercial_note_series", e.target.value)
        }
      />
      <div
        style={{
          position: "relative",
          top: "32px",
          display: "flex",
          justifyContent: "start",
        }}
      ></div>
      <Margin top={30} />
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          marginTop: "80px",
        }}
      >
        {/* <div>
          <Button
            kind="ghost"
            size="large"
            label="Cancelar"
            onClick={() => navigate("/app/credit/operations/")}
          />
        </div> */}
        <div style={{ display: "flex", gap: "10px" }}>
          {/* <div>
            <Button
              kind="tertiary"
              size="large"
              label="Voltar"
              icon={ArrowLeftIcon}
            />
          </div> */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div
            // onMouseEnter={() => setHover(true)}
            // onMouseLeave={() => setHover(false)}
            >
              {/* <Button
                kind="tertiary"
                size="large"
                icon={hover ? WhiteLeft : ArrowLeft}
                label="Voltar"
                onClick={() => {
                  navigate(
                    "/app/credit/operations/new/commercial-note/operation"
                  );
                  setTab(0);
                }}
              /> */}
            </div>
            {/* <Button
              kind="primary"
              size="large"
              label="Avançar"
              disabled={
                (values.liquidation_type === "billet" &&
                  values.invoice_barcode === "") ||
                values.debtor_uuid === "" ||
                !values.commercial_note_nature ||
                !values.commercial_note_series ||
                !values.liquidation_bank_account_uuid ||
                !values.liquidation_type
              }
              icon={ArrowIcon}
              onClick={() => {
                navigate(
                  "/app/credit/operations/new/commercial-note/involveds/"
                );
                setTab(2);
              }}
            /> */}
          </div>
        </div>
        <Margin bottom={120}/>
      </div>
  
      <div style={{ zIndex: zIndex }}>
        <LateralModal
          open={addBankAccounts}
          title="Adicionar conta bancária"
          header
          toggleModal={() => {
            handleClose();
          }}
        >
          <div style={{ padding: "20px" }}>
            <InputSelect
              label="Banco"
              kind="default"
              name="bank_identification_code"
              size="small"
              onChange={(e) =>
                setFieldValue("bank_identification_code", e.value)
              }
              options={banks}
              value=""
              placeholder={
                values.bank_identification_code !== ""
                  ? values.bank_identification_code?.split("/*?")[1]
                  : "Selecione"
              }
            />
            <Margin bottom={10} />
            <Input
              max={5}
              maxLength={5}
              name="agency"
              size="large"
              placeholder="digite"
              label="Agência"
              value={values.agency}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, "");
                setFieldValue("agency", numericValue);
              }}
            />{" "}
            <Margin bottom={10} />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                rowGap: "30px",
                height: "400px",
              }}
            >
              <Input
                name="account_number"
                size="large"
                placeholder="digite"
                max={10}
                maxLength={10}
                label="Conta"
                value={values.account_number}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setFieldValue("account_number", numericValue);
                }}
              />
              <Input
                max={1}
                maxLength={1}
                size="large"
                label="Dígito"
                placeholder="digite"
                value={values.account_number_digit}
                name="account_number_digit"
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setFieldValue("account_number_digit", numericValue);
                }}
              />
            </div>
            <Footer>
              {" "}
              <div style={{ gridColumn: "span 6" }}>
                <Button
                  kind="ghost"
                  size="large"
                  onClick={() => handleClose()}
                  label="Cancelar"
                  icon={CloseIcon}
                />
              </div>
              <div style={{ gridColumn: "span 6" }}>
                <Button
                  kind="primary"
                  size="large"
                  icon={SaveIcon}
                  onClick={() => createBankAccount()}
                  label="Salvar"
                  disabled={
                    !values.account_number ||
                    !values.agency ||
                    !values.bank_identification_code ||
                    !values.account_number_digit
                  }
                />
              </div>
            </Footer>
          </div>
        </LateralModal>
      </div>
    </Container>
  );
};

export default ProductInfo;
